import { Controller } from '@hotwired/stimulus';
import SortableList from 'stimulus/sortable_list';

const MOVABLE_SELECTOR = '.ui-draggable-card--movable';
const TITLE_SELECTOR = '.ui-draggable-card__title';

// Coordinates the cards, handing the badge numbering and the reordering to
// SortableList (shared with Ui::AccordionComponent). The whole card is the drag
// handle, and it is also the keyboard's: Alt + up/down moves whichever card
// holds focus. A move that changes the order dispatches
// `draggable-list-component:reorder` with the moved card, its old and new index
// and the ordered card ids.
export default class extends Controller {
  static targets = ['card', 'number', 'status'];

  static values = {
    numberBase: { type: Number, default: 1 }
  };

  connect() {
    this.list = new SortableList({
      element: this.element,
      movableSelector: MOVABLE_SELECTOR,
      chosenClass: 'ui-draggable-card--dragging',
      ghostClass: 'ui-draggable-card--ghost',
      ignoreAttribute: `data-${this.identifier}-ignore`,
      rows: () => this.cardTargets,
      badgeFor: (card) => this.badgeOf(card),
      titleFor: (card) => card.querySelector(TITLE_SELECTOR)?.textContent.trim() || 'Card',
      status: () => (this.hasStatusTarget ? this.statusTarget : null),
      numberBase: () => this.numberBaseValue,
      onReorder: (detail) => this.dispatch('reorder', { detail })
    });
  }

  disconnect() {
    this.list?.destroy();
    this.list = null;
  }

  cardTargetConnected() {
    this.list?.scheduleRenumber();
  }

  cardTargetDisconnected() {
    this.list?.scheduleRenumber();
  }

  reorderKeydown(event) {
    this.list?.reorderKeydown(event, (keyEvent) => {
      const row = keyEvent.target.closest(MOVABLE_SELECTOR);

      return row ? { row, focus: row } : null;
    });
  }

  // A nested list's badges belong to its own controller, so only the ones this
  // element owns take a position here.
  badgeOf(card) {
    const badge = card.querySelector(`[data-${this.identifier}-target~="number"]`);
    if (!badge) return null;

    return badge.closest(`[data-controller~="${this.identifier}"]`) === this.element ? badge : null;
  }
}
